import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ResourceCard from '@/components/feature/ResourceCard';
import { categories, resources } from '@/mocks/resources';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();

  const currentCategory = categories.find((c) => c.id === slug) || categories[0];
  const categoryResources = resources.filter((r) => r.categoryId === currentCategory.id);

  const [activeSort, setActiveSort] = useState('latest');

  const sortedResources = [...categoryResources].sort((a, b) => {
    if (activeSort === 'latest') return new Date(b.date).getTime() - new Date(a.date).getTime();
    if (activeSort === 'downloads') return b.downloads - a.downloads;
    if (activeSort === 'views') return b.views - a.views;
    return 0;
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 老六资源库`,
    description: currentCategory.description,
    url: `https://laoliu-resource.com/category/${currentCategory.id}`,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
      <Navbar />
      <main className="pt-16">
        <section className="relative w-full h-[280px] md:h-[360px] flex items-center justify-center overflow-hidden">
          <img
            src={currentCategory.image}
            alt={currentCategory.name}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/45"></div>
          <div className="relative z-10 text-center px-4">
            <h1 className="text-2xl md:text-4xl font-bold text-white mb-3 font-heading">
              {currentCategory.name}
            </h1>
            <p className="text-sm md:text-base text-white/70 max-w-md mx-auto">
              {currentCategory.description}
            </p>
          </div>
        </section>

        <div className="px-4 md:px-6 py-4 bg-background-50 border-b border-background-200/70">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory.name },
              ]}
            />
          </div>
        </div>

        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <div>
                <h2 className="text-lg font-semibold text-foreground-900">
                  共 {categoryResources.length} 个资源
                </h2>
              </div>

              <div className="flex items-center gap-1 bg-background-50 rounded-full p-1 border border-background-200/70">
                {[
                  { key: 'latest', label: '最新' },
                  { key: 'downloads', label: '下载最多' },
                  { key: 'views', label: '浏览最多' },
                ].map((sort) => (
                  <button
                    key={sort.key}
                    onClick={() => setActiveSort(sort.key)}
                    className={`px-4 py-1.5 text-xs font-medium rounded-full transition-all whitespace-nowrap cursor-pointer ${
                      activeSort === sort.key
                        ? 'bg-primary-500 text-white'
                        : 'text-foreground-600 hover:text-foreground-800'
                    }`}
                  >
                    {sort.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className={`px-4 py-2 text-xs font-medium rounded-full transition-all whitespace-nowrap ${
                    cat.id === currentCategory.id
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-50 text-foreground-600 border border-background-200/70 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {sortedResources.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                {sortedResources.map((res, index) => (
                  <div
                    key={res.id}
                    className="animate-fade-in-up"
                    style={{ animationDelay: `${index * 0.08}s` }}
                  >
                    <ResourceCard resource={res} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-200">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无资源</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}